import type { ComponentProps, ReactNode } from "react"
import classnames from "classnames"
import { kebabCase } from "lodash"
import CellTooltip from "components/securities-table/cell-tooltip"
import { COMPOSITION, filterCompareTable } from "helpers/tables"
import type { RowUnit } from "helpers/compare-composition-table"
import { buildCurrencyScaleUnit } from "helpers/metrics/currency-scale-label"
import Row from "./row"

interface PanelColumn {
  id: string
  color: string
  label: string
  type: string | undefined
  noData: boolean | undefined
  isUnmodeled: boolean | undefined
}

interface PanelItem<TData> {
  label: string
  fn: (item: TData, type?: string) => ReactNode
  tooltipContents?: ComponentProps<typeof CellTooltip>["contents"]
  unit?: RowUnit
}

interface CompareTablePanelProps<TData> {
  columns: PanelColumn[]
  items: PanelItem<TData>[]
  data: TData[]
  fixedItemNumber?: boolean
}

export default function CompareTablePanel<TData>({ items, data, columns, fixedItemNumber }: CompareTablePanelProps<TData>) {
  const filteredItems: PanelItem<TData>[] = filterCompareTable(items, data, [
    { key: COMPOSITION.REF_EQUITY, value: "--" },
    { key: COMPOSITION.MODEL_COVERAGE, value: "100.00%" },
  ])
  return <div className="compare-panel fixed">
    {filteredItems.map(({ label, fn, tooltipContents, unit }, index) => {
      const unitLabel = unit && buildCurrencyScaleUnit(unit.currencyScale, unit.currency)
      return <div key={index} className={classnames("panel-row", { "fixed-item-number": fixedItemNumber, "has-unit": unit })}>
        <div data-test-class={`${kebabCase(label)}-label`} className="panel-label" title={unitLabel && `${label} ${unitLabel}`}>
          {label}
          {tooltipContents && <CellTooltip contents={tooltipContents} color={undefined} />}
        </div>
        {unitLabel && <span className="panel-unit">{unitLabel}</span>}
        {columns.map((column, columnIndex) => <Row key={columnIndex} column={column} data={data} index={columnIndex} selector={fn} />)}
      </div>
    })}
  </div>
}
